import type { AddressForm } from "../api";

const SLOT_LABELS: Record<string, string> = {
  bscom: "BSCom",
  itamoby: "Itamoby",
  ityhome: "Ityhome",
  cosma: "Cosma",
  bizzotto: "Bizzotto",
  allservice: "AllService",
  webfurniture: "WebFurniture",
};

const ADDRESS_FIELDS: Array<{ key: keyof AddressForm; label: string; span?: boolean }> = [
  { key: "company", label: "Azienda", span: true },
  { key: "address", label: "Indirizzo", span: true },
  { key: "city", label: "Città" },
  { key: "postcode", label: "CAP" },
  { key: "state_iso", label: "Prov." },
  { key: "country_iso", label: "Paese" },
  { key: "name", label: "Nome" },
  { key: "surname", label: "Cognome" },
  { key: "phone", label: "Telefono" },
  { key: "email", label: "Email" },
];

export interface AddressPickerProps {
  slots: string[];
  value: string;
  address: AddressForm;
  /** Backend-loaded map of slot → address (from /api/returns/internal-addresses). */
  addressesBySlot: Record<string, AddressForm>;
  onPick: (slot: string) => void;
  onEdit: (field: keyof AddressForm, value: string) => void;
  label: string;
}

export function AddressPicker({
  slots,
  value,
  address,
  addressesBySlot,
  onPick,
  onEdit,
  label,
}: AddressPickerProps) {
  function handlePick(slot: string) {
    onPick(slot);
    // Pre-fill address fields from backend-loaded slot data (if loaded).
    const known = addressesBySlot[slot];
    if (known) {
      for (const { key } of ADDRESS_FIELDS) {
        onEdit(key, known[key] ?? "");
      }
    }
  }

  return (
    <div>
      <p className="mb-1.5 text-[11.5px] font-medium text-ink-2">{label}</p>
      <div className="mb-2.5 flex flex-wrap gap-1.5">
        {slots.map((slot) => {
          const active = slot === value;
          return (
            <button
              key={slot}
              type="button"
              onClick={() => handlePick(slot)}
              className={`rounded-sm border px-[10px] py-[4px] text-[11.5px] font-medium transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 ${
                active
                  ? "border-accent bg-accent-soft text-ink"
                  : "border-line bg-card text-ink-2 hover:bg-paper"
              }`}
              aria-pressed={active}
            >
              {SLOT_LABELS[slot] ?? slot}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        {ADDRESS_FIELDS.map(({ key, label: fieldLabel, span }) => (
          <label
            key={key}
            className={`flex flex-col gap-0.5 text-[11px] text-ink-2 ${span ? "col-span-2" : ""}`}
          >
            {fieldLabel}
            <input
              type="text"
              value={address[key]}
              onChange={(e) => onEdit(key, e.target.value)}
              className="rounded-sm border border-line px-2 py-1 text-[12px] text-ink focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40"
            />
          </label>
        ))}
      </div>
    </div>
  );
}
